import type { TypedSupabaseClient } from '../client';
import type {
  IProCustomerRepository,
  ProCustomer,
  ProCustomerDeletionImpact,
  RegisterProCustomerInput,
  UpdateProCustomerAdminInput,
  UpdateProCustomerInput,
} from '../../../interfaces/src';
import { fetchAllRows } from './fetch-all-rows';

export class SupabaseProCustomerRepository implements IProCustomerRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getCurrent(): Promise<ProCustomer | null> {
    const { data: userData } = await this.supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return null;

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data ? mapProCustomer(data) : null;
  }

  async createSelf(input: Omit<RegisterProCustomerInput, 'password'>): Promise<ProCustomer> {
    const { data: userData } = await this.supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) throw new Error('Not authenticated');

    const tenant = await resolveTenantBySubdomain(this.supabase, input.tenantSubdomain);
    if (!tenant) throw new Error(`Tenant introuvable: ${input.tenantSubdomain}`);

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .insert({
        user_id: userId,
        tenant_id: tenant.id,
        first_name: input.firstName,
        last_name: input.lastName,
        email: input.email,
        phone: input.phone ?? null,
        whatsapp: input.whatsapp ?? null,
        country_code: input.countryCode ?? '+216',
        registration_data: input.registrationData ?? null,
        status: 'pending',
      })
      .select()
      .single();
    if (error) throw error;
    return mapProCustomer(data);
  }

  async updateSelf(input: UpdateProCustomerInput): Promise<ProCustomer> {
    const current = await this.getCurrent();
    if (!current) throw new Error('Aucun profil pro');

    const patch: Record<string, unknown> = {};
    if (input.firstName !== undefined) patch['first_name'] = input.firstName;
    if (input.lastName !== undefined) patch['last_name'] = input.lastName;
    if (input.phone !== undefined) patch['phone'] = input.phone;
    if (input.whatsapp !== undefined) patch['whatsapp'] = input.whatsapp;
    if (input.countryCode !== undefined) patch['country_code'] = input.countryCode;
    if (input.profilePictureUrl !== undefined) patch['profile_picture_url'] = input.profilePictureUrl;
    if (input.defaultRemiseTier !== undefined) patch['default_remise_tier'] = input.defaultRemiseTier;

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .update(patch)
      .eq('id', current.id)
      .select()
      .single();
    if (error) throw error;
    return mapProCustomer(data);
  }

  async setEmailPrefs(input: {
    optout?: ProCustomer['emailOptout'];
    language?: ProCustomer['language'];
  }): Promise<ProCustomer> {
    // Passe par la RPC plutôt qu'un UPDATE direct : elle valide les catégories
    // et la langue côté serveur, et cible `current_pro_customer_id()` seule.
    const { data, error } = await (this.supabase as any).rpc('pro_set_email_prefs', {
      p_optout: input.optout ?? null,
      p_language: input.language ?? null,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return mapProCustomer(row as Record<string, unknown>);
  }

  async updateAdmin(id: string, input: UpdateProCustomerAdminInput): Promise<ProCustomer> {
    const patch: Record<string, unknown> = {};
    if (input.commissionRateOverride !== undefined) {
      // null clears the override → the account inherits the tenant default.
      // `commission_rate` (effective) is materialised by a DB trigger.
      patch['commission_rate_override'] =
        input.commissionRateOverride === null
          ? null
          : Math.min(100, Math.max(0, input.commissionRateOverride));
    }
    if (input.defaultRemiseTier !== undefined) {
      patch['default_remise_tier'] = input.defaultRemiseTier;
    }

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapProCustomer(data);
  }

  async getById(id: string): Promise<ProCustomer | null> {
    // Volontairement SANS le filtre is_preview de listForTenant : la commande
    // d'un compte Aperçu doit quand même résoudre son électricien (taux de
    // commission). Le scope tenant est garanti par la RLS.
    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data ? mapProCustomer(data) : null;
  }

  async listForTenant(filter?: { status?: ProCustomer['status'] }): Promise<ProCustomer[]> {
    // Exclude the hidden auto-provisioned "Aperçu" preview account — it's an
    // implementation detail of the owner's platform preview, not a real
    // electrician, so it must never appear in the electricians list/counts.
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = (this.supabase as any)
        .from('pro_customers')
        .select('*')
        .eq('is_preview', false);
      if (filter?.status) query = query.eq('status', filter.status);
      return query
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapProCustomer);
  }

  async approve(id: string): Promise<ProCustomer> {
    const { data, error } = await (this.supabase as any).rpc('pro_customer_approve', { p_id: id });
    if (error) throw error;
    return mapProCustomer(data as Record<string, unknown>);
  }

  async reject(id: string, reason: string): Promise<ProCustomer> {
    const { data, error } = await (this.supabase as any).rpc('pro_customer_reject', {
      p_id: id,
      p_reason: reason,
    });
    if (error) throw error;
    return mapProCustomer(data as Record<string, unknown>);
  }

  async getDeletionImpact(id: string): Promise<ProCustomerDeletionImpact> {
    // Une RPC plutôt que dix `count` côté client : un seul aller-retour, et
    // surtout `blocked`/`keepsLogin` sont calculés là où ils font autorité —
    // le même code que celui qui refusera la suppression.
    const { data, error } = await (this.supabase as any).rpc('pro_customer_deletion_impact', {
      p_id: id,
    });
    if (error) throw error;
    const row = (data ?? {}) as Record<string, unknown>;
    return {
      salesDocuments: Number(row['salesDocuments'] ?? 0),
      blocked: Boolean(row['blocked']),
      keepsLogin: Boolean(row['keepsLogin']),
      clients: Number(row['clients'] ?? 0),
      devis: Number(row['devis'] ?? 0),
      commandes: Number(row['commandes'] ?? 0),
      walletMovements: Number(row['walletMovements'] ?? 0),
      walletBalance: Number(row['walletBalance'] ?? 0),
      notifications: Number(row['notifications'] ?? 0),
      productRequests: Number(row['productRequests'] ?? 0),
      favorites: Number(row['favorites'] ?? 0),
      formationProgress: Number(row['formationProgress'] ?? 0),
    };
  }

  async delete(id: string): Promise<{ loginDeleted: boolean }> {
    // `pro_customers` n'a AUCUNE policy FOR DELETE (un `.delete()` direct part
    // en 0 ligne supprimée, silencieusement). La suppression du compte de
    // connexion demande de toute façon des droits sur `auth.users` : tout passe
    // donc par la RPC SECURITY DEFINER, qui porte aussi les refus.
    const { data, error } = await (this.supabase as any).rpc('pro_customer_delete', {
      p_id: id,
    });
    if (error) throw error;
    const row = (data ?? {}) as Record<string, unknown>;
    return { loginDeleted: Boolean(row['loginDeleted']) };
  }
}

/** Resolve the tenant a pro signup attaches to.
 *
 *  Goes through the `pro_portal_tenant` RPC rather than reading `tenants`
 *  directly: the table is anon-readable for EVERY active tenant, so the old
 *  `.from('tenants').eq('subdomain', …)` happily resolved a customer who never
 *  bought Espace Pro (and didn't even filter on `is_active`). The RPC applies
 *  both checks server-side. The `pro_customers_require_module` trigger refuses
 *  the insert anyway — this just fails earlier, with a clearer message. */
async function resolveTenantBySubdomain(
  client: TypedSupabaseClient,
  subdomain: string,
): Promise<{ id: string } | null> {
  const trimmed = subdomain.trim().toLowerCase();
  if (!trimmed) return null;
  const { data, error } = await (client as any)
    .rpc('pro_portal_tenant', { p_subdomain: trimmed })
    .maybeSingle();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data ? { id: data['id'] as string } : null;
}

function mapProCustomer(row: Record<string, unknown>): ProCustomer {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    userId: row['user_id'] as string,
    firstName: row['first_name'] as string,
    lastName: row['last_name'] as string,
    email: row['email'] as string,
    phone: (row['phone'] as string) ?? null,
    whatsapp: (row['whatsapp'] as string) ?? null,
    countryCode: (row['country_code'] as string) ?? '+216',
    status: row['status'] as ProCustomer['status'],
    profilePictureUrl: (row['profile_picture_url'] as string) ?? null,
    registrationData: (row['registration_data'] as Record<string, unknown>) ?? null,
    commissionRate: Number(row['commission_rate'] ?? 0),
    commissionRateOverride:
      row['commission_rate_override'] != null ? Number(row['commission_rate_override']) : null,
    defaultRemiseTier: parseRemiseTier(row['default_remise_tier']),
    language: parseLanguage(row['language']),
    emailOptout: Array.isArray(row['email_optout'])
      ? (row['email_optout'] as ProCustomer['emailOptout'])
      : [],
    approvedAt: (row['approved_at'] as string) ?? null,
    approvedBy: (row['approved_by'] as string) ?? null,
    rejectedReason: (row['rejected_reason'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function parseRemiseTier(value: unknown): ProCustomer['defaultRemiseTier'] {
  return value === 'min' || value === 'max' ? value : 'moy';
}

function parseLanguage(value: unknown): ProCustomer['language'] {
  return value === 'ar' || value === 'en' ? value : 'fr';
}
